import { CATEGORY_LABEL, IMPACT_META, type MarketEvent } from '@/mocks/marketCalendar';
import { minutesToLabel, toUtcMinutes } from '../calculations';
import type { EventStatus } from '../useMarketClock';
import { CurrencyFlag, ImpactDots } from './ui';

function statusMeta(status: EventStatus) {
  if (status === 'now') return { label: 'Now', cls: 'bg-violet-600 text-white' };
  if (status === 'released') return { label: 'Đã công bố', cls: 'bg-emerald-50 text-emerald-600' };
  return { label: 'Sắp diễn ra', cls: 'bg-slate-100 text-slate-500' };
}

export default function EventStream({
  events,
  toDisplay,
  statusOf,
  onSelect,
}: {
  events: MarketEvent[];
  toDisplay: (utc: number) => number;
  statusOf: (e: MarketEvent) => EventStatus;
  onSelect: (e: MarketEvent) => void;
}) {
  if (events.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-slate-50 py-14 text-center">
        <i className="ri-calendar-line text-3xl text-slate-300" />
        <p className="mt-3 text-sm text-slate-400">Không có sự kiện nào khớp với bộ lọc hiện tại.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* vertical line */}
      <div className="absolute bottom-4 left-[15px] top-4 w-px bg-slate-200" />

      <div className="space-y-3">
        {events.map((e) => {
          const meta = IMPACT_META[e.impact];
          const status = statusOf(e);
          const st = statusMeta(status);
          const displayTime = minutesToLabel(toDisplay(toUtcMinutes(e.time)));
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => onSelect(e)}
              className="group relative flex w-full items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-violet-200"
            >
              {/* node */}
              <span
                className="relative z-10 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white ring-2"
                style={{ borderColor: meta.color, color: meta.color }}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-bold tabular-nums text-slate-800">{displayTime}</span>
                  <CurrencyFlag code={e.currency} />
                  <span className="font-mono text-[11px] text-slate-400">{e.currency}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                </div>
                <p className="mt-1 text-sm font-semibold text-slate-900 group-hover:text-violet-700">{e.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                  <ImpactDots impact={e.impact} />
                  <span className="text-slate-500">{CATEGORY_LABEL[e.category]}</span>
                </div>
              </div>

              {/* data columns */}
              <div className="hidden shrink-0 grid-cols-3 gap-4 text-right md:grid">
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Trước</p>
                  <p className="mt-0.5 font-mono text-xs font-medium text-slate-600">{e.previous}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Dự báo</p>
                  <p className="mt-0.5 font-mono text-xs font-medium text-violet-600">{e.forecast}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-400">Thực tế</p>
                  {e.actual !== null ? (
                    <p className="mt-0.5 font-mono text-xs font-bold text-emerald-600">{e.actual}</p>
                  ) : (
                    <p className="mt-0.5 font-mono text-xs text-slate-300">—</p>
                  )}
                </div>
              </div>

              <i className="ri-arrow-right-s-line shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-500" />
            </button>
          );
        })}
      </div>
    </div>
  );
}